import { all, takeEvery, put, call, take } from "@redux-saga/core/effects";
import { channel } from "redux-saga";
import { AnyAction } from "redux";
import { ActionResponseType } from "../../../common/types/common.types";
import {
  SCHEDULEINSPECTIONCONFIRMATION,
  GETSCHEDULEINSPECTIONINITIALDATA,
  SAVESCHEDULEINSPECTION,
  SCHEDULEINSPECTIONNAVIGATION,
  scheduleInspectionNavigate,
} from "./ScheduleInspectionPage.action";
import { scheduleInspectionReducerActions } from "./ScheduleInspectionPage.reducer";
import { openPopup } from "../../../components/Popups/Popup.reducer";
import { EnumPopupType } from "../../../components/Popups/Popup.enum";
import { PopupProps } from "../../../components/Popups/PopupProps.types";
import { setLoading } from "../../layouts/main-layout/MainLayout.reducer";
import {
  getDuplicateInspectionNameApi,
  getScheduleInspectionInitialDataApi,
  saveScheduleInspectionApi,
} from "./ScheduleInspectionPage.api";
import { ScheduleInspectionPageType } from "./ScheduleInspectionPage.types";
import { ScheduleInspectionDTO } from "../../../common/types/common.dto.types";

// ==============================|| VIEWS - PAGES - SCHEDULE INSPECTION - SAGA ||============================== //

/**
 * A custom Redux Saga channel used for navigation-related actions.
 */
const navigationChannel = channel();

/**
 * Watches the custom navigation channel and dispatches any actions received.
 */
export function* navigationChannelWatcher() {
  while (true) {
    const action = yield take(navigationChannel);
    yield put(action);
  }
}

/**
 * Watches for navigation-related actions for the Schedule Inspection view.
 */
const scheduleInspectionNavigationWatcher = function* (): any {
  yield takeEvery(
    SCHEDULEINSPECTIONNAVIGATION,
    function* (action: AnyAction): any {
      yield put(
        scheduleInspectionReducerActions.setNavigateToViewInspectionPage(true)
      );
    }
  );
};

/**
 * Watches for GETSCHEDULEINSPECTIONINITIALDATA actions.
 */
const getScheduleInspectionInitialDataWatcher = function* (): any {
  yield takeEvery(
    GETSCHEDULEINSPECTIONINITIALDATA,
    function* (action: AnyAction): any {
      // set page loading
      yield put(setLoading(true));

      const inspectionSetId: number = action.payload;

      const result: ActionResponseType = yield call(() =>
        getScheduleInspectionInitialDataApi(inspectionSetId)
      );

      if (!result.IsSuccess) {
        yield put(
          openPopup({
            Open: true,
            BodyText: "An error occurred when getting data",
            HeaderText: "Warning",
            PopupType: EnumPopupType.WarningPopup,
          } as PopupProps)
        );
      } else {
        const scheduleInspectionData = {
          ...result.Data,
        } as ScheduleInspectionDTO;

        yield put(
          scheduleInspectionReducerActions.setScheduleInspectionInitialState({
            ScheduleInspection: {
              ...scheduleInspectionData,
              InspectionName: "",
              PlannedCompletionDate: null,
            } as ScheduleInspectionDTO,
          } as ScheduleInspectionPageType)
        );
      }

      // set page loading
      yield put(setLoading(false));
    }
  );
};

/**
 * Watches for SAVESCHEDULEINSPECTION actions.
 */
const saveScheduleInspectionWatcher = function* (): any {
  yield takeEvery(SAVESCHEDULEINSPECTION, function* (action: AnyAction): any {
    yield put(setLoading(true));

    const scheduleInspection: ScheduleInspectionDTO = action.payload;
    try {
      let result: ActionResponseType = yield call(() =>
        saveScheduleInspectionApi(scheduleInspection)
      );

      if (result.IsSuccess) {
        yield put(
          openPopup({
            Open: true,
            BodyText: result.InfoMessage || "Inspection(s) saved successfully",
            HeaderText: "Success",
            PopupType: EnumPopupType.SuccessPopup,
            OkBtnClick() {
              navigationChannel.put(scheduleInspectionNavigate());
            },
          } as PopupProps)
        );
      } else {
        yield put(
          openPopup({
            Open: true,
            BodyText: "An error occured when saving the inspection",
            HeaderText: "Error",
            PopupType: EnumPopupType.ErrorPopup,
          } as PopupProps)
        );
      }
    } catch (error) {
      yield put(
        openPopup({
          Open: true,
          BodyText: "An error occured when saving the inspection",
          HeaderText: "Error",
          PopupType: EnumPopupType.ErrorPopup,
        } as PopupProps)
      );
    }

    yield put(setLoading(false));
  });
};

/**
 * Watches for SCHEDULEINSPECTIONCONFIRMATION actions.
 */
const scheduleInspectionConfirmationWatcher = function* (): any {
  yield takeEvery(
    SCHEDULEINSPECTIONCONFIRMATION,
    function* (action: AnyAction): any {
      yield put(setLoading(true));

      const { scheduleInspection, onConfirmationCallBack } = action.payload;
      try {
        let result: ActionResponseType = yield call(() =>
          getDuplicateInspectionNameApi(scheduleInspection)
        );

        if (result.IsSuccess) {
          const duplicateNames: Array<string> =
            result.Data ?? ([] as Array<string>);

          if (duplicateNames?.length > 0) {
            yield put(
              openPopup({
                Open: true,
                BodyText: `<p>The Inspection name(s) you entered already exists. Duplicate names are not allowed. Please update the name(s) to be unique.</p>
                    <br/><p class="text-bold">The following name(s) are duplicated:</p><br/>
                    <p class="body2">${duplicateNames
                      ?.map((name) => {
                        return name;
                      })
                      ?.join(" , ")}</p>`,
                HeaderText: "Warning",
                PopupType: EnumPopupType.WarningPopup,
                Size: "modal-lg",
              } as PopupProps)
            );
          } else {
            if (!onConfirmationCallBack) return;

            onConfirmationCallBack(scheduleInspection); // call back method
          }
        } else {
          yield put(
            openPopup({
              Open: true,
              BodyText: "An error occurred when getting data",
              HeaderText: "Error",
              PopupType: EnumPopupType.ErrorPopup,
            } as PopupProps)
          );
        }
      } catch (error) {
        yield put(
          openPopup({
            Open: true,
            BodyText: "An error occurred when getting data",
            HeaderText: "Error",
            PopupType: EnumPopupType.ErrorPopup,
          } as PopupProps)
        );
      }

      yield put(setLoading(false));
    }
  );
};

/**
 * Root saga that combines all schedule inspection related watchers.
 */
const scheduleInspectionSaga = function* () {
  yield all([
    getScheduleInspectionInitialDataWatcher(),
    saveScheduleInspectionWatcher(),
    navigationChannelWatcher(),
    scheduleInspectionNavigationWatcher(),
    scheduleInspectionConfirmationWatcher(),
  ]);
};

export default scheduleInspectionSaga;
